// Pieces shared by the AI team Overview and each team's page: number cards,
// decisions with Approve / Reject, the job form and the team on/off switch.
// Everything happens on the page: no pop-ups or side sheets.
import { CountUp } from '@/components/primitives/CountUp'
import { Switch } from '@/components/ui/switch'
import {
  type ApprovalItem,
  type TeamAgent,
  createRoom,
  decideCard,
  sendMessage
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { runRecordAction } from '@/lib/sheet'
import { cn } from '@/lib/utils'
import { AgentAvatar } from '@/pages/ai-team/AgentAvatar'
import { type Entry, entriesOf, refresh } from '@/pages/ai-team/DecisionDock'
import { CHIP, type Hub, type Tone, card, hubAgents, totals } from '@/pages/ai-team/hubs'
import { timeAgo } from '@/pages/ai-team/workflows/shared'
import type { ResultRef } from './ResultCard'
import { Briefcase, Check, CheckCircle2, ChevronLeft, ChevronRight, Loader2, type LucideIcon, X } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

export const field =
  'w-full rounded-[36px] border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-emerald-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:placeholder:text-neutral-500'
export const sectionTitle = 'text-lg font-bold text-neutral-900 dark:text-white'
export const goBtn =
  'inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-neutral-950 transition-all hover:bg-emerald-400 active:scale-95 disabled:opacity-50'
export const ghostBtn =
  'inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800'

export const teamPath = (hub: Hub) => `/apps/ai-team/team/${hub.id}`

/** The agent a team's requests go to first (the front door for Support). */
export const leadOf = (hub: Hub, agents: TeamAgent[]) =>
  hubAgents(hub, agents).find((a) => a.enabled) ?? hubAgents(hub, agents)[0]

/** Toast after work starts. The result shows on the page; nothing navigates. */
export function started(who: string) {
  toast.success(`${who} is on it`, { description: 'The result shows below. Anything that changes something waits for your yes.' })
}

export function Stat({
  label,
  value,
  icon: Icon,
  tone,
  format,
  progress
}: {
  label: string
  value: number
  icon: LucideIcon
  tone: Tone
  format?: (n: number) => string
  /** 0–1: shows a bar, red past 90%. */
  progress?: number
}) {
  const pct = Math.min(100, (progress ?? 0) * 100)
  return (
    <div className={cn(card, 'min-w-0')}>
      <span className={cn('flex size-9 items-center justify-center rounded-full', CHIP[tone])}>
        <Icon className="size-4" />
      </span>
      <span className="mt-3 block text-3xl font-bold leading-none tabular-nums text-neutral-900 dark:text-white">
        <CountUp value={value} format={format} />
      </span>
      <span className="mt-1.5 block truncate text-xs font-semibold text-neutral-500 dark:text-neutral-400">{label}</span>
      {progress !== undefined && (
        <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
          <span
            className={cn('block h-full rounded-full', pct > 90 ? 'bg-red-500' : 'bg-emerald-500')}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </span>
      )}
    </div>
  )
}

export function Decisions({
  items,
  loading,
  title = 'Needs your decision',
  perPage = 10
}: {
  items: ApprovalItem[]
  loading: boolean
  title?: string
  perPage?: number
}) {
  const entries = entriesOf(items)
  const [page, setPage] = useState(1)
  const pages = Math.max(1, Math.ceil(entries.length / perPage))
  const current = Math.min(page, pages)
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className={sectionTitle}>
          {title}
          {entries.length > 0 && (
            <span className="ml-2 text-sm font-semibold tabular-nums text-neutral-500 dark:text-neutral-400">{entries.length}</span>
          )}
        </h2>
        {pages > 1 && <Pager page={current} pages={pages} onPage={setPage} />}
      </div>
      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className={cn(card, 'flex items-center gap-3')}>
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full', CHIP.emerald)}>
            <CheckCircle2 className="size-5" />
          </span>
          <div>
            <p className="font-bold text-neutral-900 dark:text-white">All clear</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing is waiting for you.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.slice((current - 1) * perPage, current * perPage).map((e) => (
            <DecisionCard key={e.key} entry={e} />
          ))}
        </div>
      )}
    </section>
  )
}

/** One decision: Approve / Reject with an optional direction for the agent. */
export function DecisionCard({ entry }: { entry: Entry }) {
  const [pending, setPending] = useState<'approve' | 'reject' | 'answer' | null>(null)
  const [note, setNote] = useState('')
  // Several questions: one pick per question, sent together.
  const [picks, setPicks] = useState<Record<number, string>>({})
  const list = entry.kind === 'group' ? entry.items : [entry.item]
  const first = list[0]
  const hot = first.money || first.severity === 'urgent'
  const questions = first.questions ?? []
  const several = entry.kind === 'one' && questions.length > 1
  const answer = several
    ? [...questions.map((_, i) => picks[i]).filter(Boolean), note.trim()].filter(Boolean).join('; ')
    : note.trim()

  async function decideOne(id: number, decision: 'approve' | 'reject' | 'answer') {
    const card = await decideCard(id, decision, answer)
    // The yes went through, but what it was meant to do didn't.
    if (card?.status === 'failed') throw new Error(card.error || 'The action failed')
    return card
  }

  async function decide(decision: 'approve' | 'reject' | 'answer') {
    if (pending) return
    setPending(decision)
    try {
      const results = await Promise.allSettled(list.map((i) => decideOne(i.id, decision)))
      const ok = results.filter((r) => r.status === 'fulfilled').length
      const firstError = results.find((r): r is PromiseRejectedResult => r.status === 'rejected')?.reason
      if (ok === list.length) {
        toast.success(decision === 'approve' ? 'Approved' : decision === 'reject' ? 'Rejected' : 'Answer sent')
        setNote('')
        setPicks({})
      } else if (ok === 0) {
        notifyError('Could not save your decision', firstError)
      } else {
        toast.error(`${ok} of ${list.length} saved`, {
          description: firstError instanceof Error ? firstError.message : undefined
        })
      }
    } finally {
      setPending(null)
      await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: ['ai-work'] })]).catch(() => undefined)
    }
  }

  const approveLabel =
    entry.kind === 'group'
      ? `Approve all ${list.length}`
      : first.money && first.price?.label
        ? `Approve ${first.price.label}`
        : 'Approve'
  const isApproval = first.kind === 'approval'

  return (
    <div className={cn(card, 'space-y-3', hot && 'border-red-300 dark:border-red-500/40')}>
      <div className="flex items-start gap-3">
        <AgentAvatar agentKey={first.agent.key} name={first.agent.name} size="md" state="waiting" />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-white">
            {entry.kind === 'group' ? `${entry.label} (${list.length})` : first.title}
          </p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {first.agent.name} · {timeAgo(first.created_at)}
          </p>
        </div>
        {hot && (
          <span className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold', CHIP.red)}>
            {first.money ? 'Money' : 'Urgent'}
          </span>
        )}
      </div>
      {entry.kind === 'one' && first.why && (
        <p className="line-clamp-3 text-sm text-neutral-600 dark:text-neutral-300">{first.why}</p>
      )}
      {several && !isApproval && (
        <div className="space-y-3">
          {questions.map((q, qi) => (
            <div key={qi} className="space-y-1.5">
              <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{q.question}</p>
              {(q.options ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {q.options.slice(0, 6).map((o) => (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={picks[qi] === o}
                      onClick={() => setPicks((p) => ({ ...p, [qi]: p[qi] === o ? '' : o }))}
                      className={cn('rounded-full px-3 py-1 text-xs font-semibold', picks[qi] === o ? CHIP.emerald : CHIP.neutral)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {entry.kind === 'one' && questions.length === 1 && !isApproval && (
        <div className="flex flex-wrap gap-1.5">
          {(questions[0].options ?? []).slice(0, 6).map((o) => (
            <button key={o} type="button" onClick={() => setNote(o)} className={cn('rounded-full px-3 py-1 text-xs font-semibold', note === o ? CHIP.emerald : CHIP.neutral)}>
              {o}
            </button>
          ))}
        </div>
      )}
      <label className="block">
        <span className="sr-only">
          {isApproval ? 'Direction for the agent (optional)' : first.kind === 'alert' ? 'A note (optional)' : 'Your answer'}
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            isApproval
              ? 'Add a direction (optional), e.g. “only refund $20”'
              : first.kind === 'alert'
                ? 'A note (optional)'
                : several
                  ? 'Anything else (optional)'
                  : 'Your answer'
          }
          className={field}
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        {isApproval ? (
          <>
            <button type="button" onClick={() => decide('approve')} disabled={!!pending} className={goBtn}>
              {pending === 'approve' ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              {approveLabel}
            </button>
            <button
              type="button"
              onClick={() => decide('reject')}
              disabled={!!pending}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:hover:border-red-500/40 dark:hover:text-red-400"
            >
              {pending === 'reject' ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
              {entry.kind === 'group' ? 'Reject all' : 'Reject'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => decide('answer')}
            disabled={!!pending || (first.kind === 'question' && !answer)}
            className={goBtn}
          >
            {pending === 'answer' ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {first.kind === 'question' ? 'Send answer' : 'Got it'}
          </button>
        )}
      </div>
    </div>
  )
}

/** Pick a job, its topic, an optional note, Start. Shown right on the team page. */
export function JobForm({
  hub,
  agents,
  onStarted
}: {
  hub: Hub
  agents: TeamAgent[]
  onStarted: (r: ResultRef) => void
}) {
  const jobs = agents
    .filter((a) => a.enabled)
    .flatMap((a) => a.quick_tasks.map((task) => ({ id: `${a.key}:${task.id ?? task.label}`, agent: a, task })))
  const [jobId, setJobId] = useState('')
  const [choice, setChoice] = useState('')
  const [note, setNote] = useState('')
  const [pending, setPending] = useState(false)
  const job = jobs.find((j) => j.id === jobId) ?? jobs[0]

  async function start(e: FormEvent) {
    e.preventDefault()
    if (!job) return
    setPending(true)
    try {
      const pick = choice || job.task.choice?.options[0] || ''
      const text = job.task.task.replace('{choice}', pick) + (note.trim() ? `\n\n${note.trim()}` : '')
      const room = await createRoom(job.agent.key)
      const sent = await sendMessage(room.id, text)
      setNote('')
      started(job.agent.name)
      onStarted({
        roomId: room.id,
        afterId: sent.id,
        ids: [sent.id],
        title: job.task.label + (pick ? ` · ${pick}` : ''),
        agentKey: job.agent.key,
        agentName: job.agent.name,
        at: new Date().toISOString()
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-rooms'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-team'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-work'] })
      ])
    } catch (err) {
      notifyError(`Could not start "${job.task.label}"`, err)
    } finally {
      setPending(false)
    }
  }

  if (jobs.length === 0)
    return (
      <div className={card}>
        <p className={sectionTitle}>Give {hub.title} a job</p>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {agents.some((a) => a.enabled)
            ? 'No jobs yet. Add some in Settings → Jobs.'
            : 'This team is switched off. Switch it on to give it a job.'}
        </p>
      </div>
    )

  return (
    <form onSubmit={start} className={cn(card, 'space-y-3')} id="give-a-job">
      <p className={sectionTitle}>Give {hub.title} a job</p>
      <div className="grid gap-3">
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Job</span>
          <select
            value={job?.id}
            onChange={(e) => {
              setJobId(e.target.value)
              setChoice('')
            }}
            className={field}
          >
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.task.label} · {j.agent.name}
              </option>
            ))}
          </select>
        </label>
        {job?.task.choice && (
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">{job.task.choice.label}</span>
            <select value={choice || job.task.choice.options[0]} onChange={(e) => setChoice(e.target.value)} className={field}>
              {job.task.choice.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. only Atlanta" className={field} />
        </label>
      </div>
      <button type="submit" disabled={pending} className={goBtn}>
        {pending ? <Loader2 className="size-4 animate-spin" /> : <Briefcase className="size-4" />}
        Start
      </button>
    </form>
  )
}

/** On/off for every agent in a team. */
export function TeamToggle({ hub, agents }: { hub: Hub; agents: TeamAgent[] }) {
  const on = agents.some((a) => a.enabled)
  const [busy, setBusy] = useState(false)
  async function toggle(next: boolean) {
    setBusy(true)
    try {
      for (const a of agents.filter((x) => x.enabled !== next))
        await runRecordAction('super_agents__agentprofile', a.id, next ? 'turn_on' : 'turn_off', {})
      toast.success(`${hub.title} is ${next ? 'on' : 'off'}`)
    } catch (err) {
      notifyError(`Could not switch ${hub.title}`, err)
    } finally {
      setBusy(false)
      // Some agents may have switched before a failure: always show the truth.
      void queryClient.invalidateQueries({ queryKey: ['ai-team'] })
    }
  }
  return (
    <label className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
      {busy && <Loader2 className="size-3.5 animate-spin" />}
      {on ? 'On' : 'Off'}
      <Switch checked={on} disabled={busy} onCheckedChange={toggle} aria-label={`${hub.title} on or off`} />
    </label>
  )
}

/** What a team's status pill says. */
export function teamStatus(agents: TeamAgent[]): { text: string; tone: Tone } {
  const t = totals(agents)
  if (!agents.some((a) => a.enabled)) return { text: 'Off', tone: 'neutral' }
  if (t.working > 0) return { text: `${t.working} working`, tone: 'emerald' }
  if (t.waiting > 0) return { text: `${t.waiting} need you`, tone: 'amber' }
  if (t.failed > 0) return { text: `${t.failed} failed today`, tone: 'red' }
  return { text: 'Ready', tone: 'neutral' }
}

/** Previous / next with "page X of Y". */
export function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  return (
    <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className="inline-flex size-8 items-center justify-center rounded-full hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
      >
        <ChevronLeft className="size-4" />
      </button>
      <span className="tabular-nums">
        {page} of {pages}
      </span>
      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={page >= pages}
        aria-label="Next page"
        className="inline-flex size-8 items-center justify-center rounded-full hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  )
}
